import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { Box, Divider, FormControl, FormLabel, Grid, Paper, styled } from "@mui/material";
import StdFormDialog from "components/common/form/StdFormDialog";
import { TCard, useCards } from "models/CardModel";
import { useCallback, useEffect, useState } from "react";
import { atom, useRecoilState, useRecoilValue, useResetRecoilState } from "recoil";
import { CSS } from "@dnd-kit/utilities";
import { useCardMasters } from "models/CardMasterModel";

export const sortKamokuCardAtom = atom<TCard>({
  key: "CardSortKamokuFormDialog/Atom/card",
  default: undefined,
});

export const sortKamokuOpenAtom = atom<boolean>({
  key: "CardSortKamokuFormDialog/Atom/open",
  default: false,
});

export const CardSortKamokuFormDialog = () => {
  const [open, setOpen] = useRecoilState(sortKamokuOpenAtom);
  const card = useRecoilValue(sortKamokuCardAtom);
  const resetCard = useResetRecoilState(sortKamokuCardAtom);
  const [list, setList] = useState<any[]>([]);
  const { saveCard } = useCards();

  const { getCardMaster } = useCardMasters();
  const cardMaster = card ? getCardMaster(card.card_master_id) : undefined;

  const handleOnClose = useCallback(() => {
    setOpen(false);
    resetCard();
  }, [resetCard, setOpen]);

  const handleOnSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const newCard = { ...card };
      newCard.kamoku_sort = list.map((item) => item.id);
      saveCard(newCard);
      handleOnClose();
    },
    [card, handleOnClose, list, saveCard],
  );

  const handleOnReset = useCallback(() => {
    const newCard = { ...card };
    newCard.kamoku_sort = undefined;
    saveCard(newCard);
    handleOnClose();
  }, [card, handleOnClose, saveCard]);

  useEffect(() => {
    const fetchAndSetKamokuList = () => {
      if (!card || !cardMaster) return;

      // 全科目リストを取得
      const allKamokuList = cardMaster.targets;
      const allKamokuKeys = Object.keys(allKamokuList);

      // card.kamoku_sort に含まれるIDで、現存するものだけを先頭に並べる
      const sortedKamoku =
        card.kamoku_sort
          ?.filter((id) => allKamokuKeys.includes(id))
          .map((id) => ({
            id,
            value: allKamokuList[id as keyof typeof allKamokuList],
          })) ?? [];

      // まだ並び順に含まれていない科目を後ろに追加（表示科目が選択されている場合は表示課目に含まれている科目だけ）
      const unsortedKamoku = allKamokuKeys
        .filter((key) => !card.kamoku_list || card.kamoku_list.includes(key))
        .filter((key) => !card.kamoku_sort?.includes(key))
        .map((key) => ({
          id: key,
          value: allKamokuList[key as keyof typeof allKamokuList],
        }));

      setList([...sortedKamoku, ...unsortedKamoku]);
    };

    fetchAndSetKamokuList();
  }, [card, cardMaster]);

  const handleOnDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setList((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  if (!card || list.length === 0) return null;

  return (
    <StdFormDialog
      title="表示順変更"
      openState={open}
      handleOnSubmit={handleOnSubmit}
      handleOnReset={handleOnReset}
      handleOnClose={handleOnClose}
    >
      <Divider />

      <FormControl component="fieldset" fullWidth sx={{ marginTop: 2, marginBottom: 2 }}>
        <FormLabel component="legend">科目 並び順</FormLabel>
        <div>
          <DndContext onDragEnd={handleOnDragEnd}>
            <SortableContext items={list}>
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {list.map((kamoku, index) =>
                  kamoku.value ? (
                    <SortableItem key={kamoku.id} id={kamoku.id} label={kamoku.value.name} index={index} />
                  ) : null,
                )}
              </Grid>
            </SortableContext>
          </DndContext>
        </div>
      </FormControl>
    </StdFormDialog>
  );
};

const SortableItem = ({ id, label, index }: { id: any; label: string; index: number }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Grid
      item
      xs={3}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{ zIndex: isDragging ? 1 : "unset", cursor: isDragging ? "grabbing" : "grab" }}
    >
      <Item>
        <Box sx={{ display: "flex", textAlign: "left" }}>
          <Box sx={{ width: "20%" }}>{index + 1}.</Box>
          <Box>{label}</Box>
        </Box>
      </Item>
    </Grid>
  );
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  ...theme.applyStyles("dark", {
    backgroundColor: "#1A2027",
  }),
}));
